import React, { useEffect, useRef, useState } from 'react';
import './progress-summary.styles.scss';
import { useParams } from 'react-router-dom';
import { getCompiledDataFromDb, getProgressFromDb } from '@/utils/getDataFromDb';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { createPurchaseDataPoint } from '@/utils/create-purchase-data-point';
import { format } from 'date-fns';
import { properCase } from '@/utils/properCase';
import SubmissionsRemainingModal from './submissions-remaining-modal.component';

const ProgressSummary = ({
    user,
    mcData,
    contentCount
}) => {

    const { progressId } = useParams();
    const [ progress, setProgress ] = useState(null);
    const [ mcStatus, setMcStatus ] = useState(null);
    const [ timeline, setTimeline ] = useState(null);
    const [ progressValue, setProgressValue ] = useState(0);
    const [ approvalValue, setApprovalValue ] = useState(0);
    const [ showSubmissionsRemainingModal, setSubmissionsRemainingModal ] = useState(false);
    const timelineRef = useRef();
    let progressListener;
    let timelineListener;

    useEffect(() => {

        return () => {
            if (progressListener) progressListener();
            if (timelineListener) timelineListener();
        }
    }, [])

    useEffect(() => {
        if (!progressId || !user || !mcData) return;
        getProgress();
        getTimeline();
    }, [progressId, user, mcData])

    useEffect(() => {
        if (!progress || !contentCount) return;
        getStatus();
        getPercents();
    }, [progress, contentCount])

    function getProgress() {
        getProgressFromDb({
            'progressId': progressId,
            'callback': callback,
            'handleListener': handleListener
        })
        function callback(data) {
            setProgress(data);
        }
        function handleListener(unsub) {
            progressListener = unsub;
        }
    }

    function getStatus() {
        if (progress.deleted) {
            setMcStatus({
                'text': 'deleted',
                'date': `Deleted: ${format(new Date(progress.ts_deleted), "Pp")}`
            })
            return;
        }
        else if (progress.approved) {
            setMcStatus({
                'text': 'complete',
                'date': `Approved: ${format(new Date(progress.lastEval), "Pp")}`
            })
        } else if (progress.pending) {
            setMcStatus({
                'text': 'pending',
                'date': `Submitted: ${format(new Date(progress.lastEdit), "Pp")}`
            })
        } else if (progress.declined) {
            setMcStatus({
                'text': 'declined',
                'date': `Evaluated: ${format(new Date(progress.lastEval), "Pp")}`
            })
        } else {
            setMcStatus({
                'text': 'in progress',
                'date': `Last Edit: ${format(new Date(progress.lastEdit), "Pp")}`
            })
        }
    }

    function getTimeline() {
        getPurchaseDate();
        getCompiledDataFromDb({
            'progressId': progressId,
            'callback': callback,
            'handleListener': handleListener
        })
        function callback(data) {
            timelineRef.current = {
                ...timelineRef.current,
                ...{[data.id]: data}
            }
            setTimeline(timelineRef.current);
        }
        function handleListener(unsub) {
            timelineListener = unsub;
        }
    }

    async function getPurchaseDate() {
        const res = await UseCloudFunction(
            'getMcUserListFromNfe',
            {
                'profile': user,
                'mcData': mcData
            }
        )
        const purchase = res.filter(r => r.cst_key === user.cst_key)[0];
        if (!purchase) return;
        const obj = createPurchaseDataPoint(purchase, user, mcData);
        timelineRef.current = {
            ...timelineRef.current,
            ...{[obj.id]: obj}
        }
        setTimeline(timelineRef.current);
    }

    function getPercents() {
        let completeCount = 0;
        let approvedCount = 0;
        let approved = []
        for (let p of Object.keys(progress)) {
            if (p.includes('content_')) {
                completeCount++;
                const id = p.replace('content_', '');
                if (
                    progress[p].approved ||
                    (
                        progress[`eval_${id}`] &&
                        (
                            progress[`eval_${id}`].approved ||
                            (
                                progress[`eval_${id}`].completed &&
                                progress[`eval_${id}`].explained &&
                                progress[`eval_${id}`].located
                            )
                        )
                    )
                ) {
                    approvedCount++;
                    approved.push(id);
                }
            }
        }
        const compPercent = (((completeCount - 1)/contentCount) * 100).toFixed(0).toLocaleString();
        setProgressValue(compPercent);
        const apprPercent = ((approvedCount/contentCount) * 100).toFixed(0).toLocaleString();
        setApprovalValue(apprPercent);
        document.getElementById("complete-progress").style.width = compPercent + "%";
        document.getElementById("approved-progress").style.width = apprPercent + "%";
    }

    return (
        <div className='progress-summary'>
            <div className='status-box'>
                <div className='g-list-item'>
                    MC Status
                    {
                        (mcStatus) &&
                        <>
                        <div className="g-space-0-5"></div>
                        <div className={`status ${mcStatus.text.replace(' ', '')}`}>
                            {mcStatus.text}
                        </div>
                        <div className='meta'>{mcStatus.date}</div>
                        </>
                    }
                </div>
                <div className='g-list-item attempts-remaining'>
                    {
                        (progress) && 
                        <>
                        <div className='meta'>Attempts<br />Remaining</div>
                        <div>{progress.submissionsRemaining}</div>
                        {
                            (location.pathname.includes('/admin/progress/')) &&
                            <button 
                                className='g-button text-only'
                                onClick={() => setSubmissionsRemainingModal(true)}>
                                Change
                            </button>
                        }
                        </>
                    }
                </div>
            </div>
            <div className='progress-with-desc complete-box g-list-item'>
                User Completion
                <div className="g-space-0-5"></div>
                <div className='progress-container'>
                    <div className="progress">
                        <div 
                            className="progress-bar" 
                            id='complete-progress' 
                            aria-valuenow={progressValue} 
                            min="0" 
                            max="100" >
                        </div>
                    </div>
                    <div className='meta'>{progressValue}%</div>
                </div>
            </div>
            <div className='progress-with-desc eval-box g-list-item'>
                Evaluator Approval
                <div className="g-space-0-5"></div>
                <div className='progress-container'>
                    <div className="progress">
                        <div 
                            className="progress-bar"
                            id='approved-progress' 
                            aria-valuenow={approvalValue} 
                            min="0" 
                            max="100" >
                        </div>
                    </div>
                    <div className='meta'>{approvalValue}%</div>
                </div>
            </div>
            <section className='timeline-list g-list-item'>
                Timeline:
                {
                    (timeline) &&
                    Object.values(timeline)
                    .sort((a,b) => a.ts < b.ts ? 1 : -1)
                    .map(t => {
                        return (
                            <div 
                                key={t.id}
                                className='timeline-item meta'>
                                <div>{format(new Date(t.ts), "Pp")}</div>
                                <div>{properCase(t.type.replace('-', ' '))}</div>
                            </div>
                        )
                    })
                }
            </section>   
            <SubmissionsRemainingModal
                show={showSubmissionsRemainingModal}
                cancel={() => setSubmissionsRemainingModal(false)}
                progress={progress} />
        </div>
    )
}

export default ProgressSummary